// The exported code uses Tailwind CSS. Install Tailwind CSS in your dev environment to ensure all styles work.

import React, { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import Login from "./pages/Login";
import Home from "./pages/Home";
import LearningDetail from "./pages/LearningDetail";
import Sidebar from "./components/Sidebar";
import Layout from "./components/Layout";
import { useAuth } from "./contexts/AuthContext";
import { DarkModeProvider } from "./contexts/DarkModeContext";
import { LearningProvider } from "./contexts/LearningContext";
import LandingPage from "./pages/LandingPage";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";

const LoadingScreen: React.FC = () => {
  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-slate-50 dark:bg-gray-950">
      <div className="w-10 h-10 border-2 border-t-[#47b3fe] border-slate-200 dark:border-gray-800 rounded-full animate-spin"></div>
    </div>
  );
};

// Login route with redirect
const LoginRoute: React.FC = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const checkoutUrl = (location.state as { checkoutUrl?: string } | null)
    ?.checkoutUrl;

  // If the user arrived from the pricing CTA, resume checkout once signed in —
  // appending their account email/name so the Lemon Squeezy order matches.
  useEffect(() => {
    if (user && checkoutUrl) {
      const separator = checkoutUrl.includes("?") ? "&" : "?";
      window.location.href = `${checkoutUrl}${separator}checkout[email]=${encodeURIComponent(
        user.email || ""
      )}&checkout[name]=${encodeURIComponent(
        user.displayName || ""
      )}&checkout[custom][firebase_uid]=${encodeURIComponent(user.uid)}`;
    }
  }, [user, checkoutUrl]);

  if (loading) {
    return <LoadingScreen />;
  }

  // Signed in with a pending checkout — show loader while we redirect out.
  if (user && checkoutUrl) {
    return <LoadingScreen />;
  }

  if (user) {
    return <Navigate to="/home" />;
  }

  return <Login />;
};

// Update AuthenticatedLayout
const AuthenticatedLayout: React.FC = () => {
  const { user, loading } = useAuth();

  // Show loading screen while checking auth state
  if (loading) {
    return <LoadingScreen />;
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" />;
  }

  return (
    <div className="flex h-screen bg-slate-50 dark:bg-gray-950 relative transition-colors duration-200">
      <Sidebar />
      <main className="flex-1 pl-64 w-full overflow-y-auto overflow-x-hidden">
        <Routes>
          <Route
            path="/"
            element={
              <Layout>
                <Home />
              </Layout>
            }
          />
          <Route path="/learning/:id" element={<LearningDetail />} />
          <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
      </main>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <DarkModeProvider>
          <SubscriptionProvider>
            <LearningProvider>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<LoginRoute />} />
                <Route path="/home/*" element={<AuthenticatedLayout />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
              </Routes>
            </LearningProvider>
          </SubscriptionProvider>
        </DarkModeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
